import React, { useState, useContext, useRef } from 'react';
import styles from './styles/CreateClassroomModal.module.css';
import { ClassroomContext } from '../../context/ClassroomContext';

const CreateClassroomModal = ({ isOpen, onClose, onClassroomCreated }) => {
    const [classroomName, setClassroomName] = useState('');
    const [image, setImage] = useState(null); 
    const [imagePreview, setImagePreview] = useState(null); 
    const [error, setError] = useState(null);
    const [closing, setClosing] = useState(false);
    const { createClassroom } = useContext(ClassroomContext);

    const fileInputRef = useRef(null); 

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            
            if (file.size > 5 * 1024 * 1024) { 
                setError('Image size should not exceed 5MB.');
                setImage(null); 
                setImagePreview(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = ""; 
                }
                return;
            }
            if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
                setError('Invalid file type. Please upload an image (JPG, PNG, GIF, WEBP).');
                setImage(null); 
                setImagePreview(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = ""; 
                }
                return;
            }
            setError(null); 
            setImage(file); 
            setImagePreview(URL.createObjectURL(file)); 
        } else {
            setImage(null);
            setImagePreview(null);
        }
    };

    const handleRemoveImage = () => {
        setImage(null);         
        setImagePreview(null);  
        if (fileInputRef.current) {
            fileInputRef.current.value = "";                       
        }
        setError(null); 
    };

    const handleCreate = async () => {
        if (!classroomName.trim()) {
            setError('Classroom name cannot be empty.');
            return;
        }
        setError(null); 
        try {
            const newClassroom = await createClassroom({
                name: classroomName,
                image: image, 
            });
            onClassroomCreated(newClassroom._id);         
            triggerClose(); 
        } catch (error) {
            console.error("Error creating classroom:", error); 
            const errorMessage = error.response?.data?.errorMessage || error.message || 'Failed to create the classroom. Please try again.';
            setError(errorMessage);
        }
    };

    const triggerClose = () => {
        setClosing(true);
        setTimeout(() => {
            onClose(); 
            setClassroomName('');
            setImage(null);
            setImagePreview(null);
            setError(null);
            setClosing(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; 
            }
        }, 300); 
    };

    
    
    React.useEffect(() => {
        return () => {
            if (imagePreview && imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);


    if (!isOpen && !closing) return null;

    return (
        <div className={`${styles.modalOverlay} ${closing ? styles.fadeOut : styles.fadeIn}`}>
            <div className={`${styles.modalContent} ${styles.wideModal}`}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Create Classroom</h2>
                </div>

                {/* Modal Body */}
                <div className={styles.modalBody}>
                    <input
                        type="text"
                        className={styles.modalInput}
                        value={classroomName}
                        onChange={(e) => setClassroomName(e.target.value)}
                        placeholder="Classroom Name"
                        aria-label="Classroom Name"
                    />

                    <input
                        type="file"
                        id="classroomImageUpload"
                        ref={fileInputRef} 
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        accept="image/png, image/jpeg, image/gif, image/webp"
                    />

                    <label htmlFor="classroomImageUpload" className={styles.uploadImageButton}>
                        {imagePreview ? 'Change Classroom Image' : 'Add Classroom Image'}
                    </label>

                    {imagePreview && (
                        <div className={styles.imagePreviewContainer}>
                            <img src={imagePreview} alt="Classroom Preview" className={styles.imagePreview} />
                            <button
                                onClick={handleRemoveImage} 
                                className={styles.removeImageButton}
                                aria-label="Remove classroom image"
                            >
                                Remove Image
                            </button>
                        </div>
                    )}

                    {error && <div className={styles.error}>{error}</div>}
                </div>

                {/* Modal Footer */}
                <div className={styles.modalFooter}>
                    <div className={styles.buttonContainer}>
                        <button className={`${styles.buttonBase} ${styles.saveButton}`} onClick={handleCreate} disabled={!classroomName.trim() && !image}>
                            Create
                        </button>
                        <button className={`${styles.buttonBase} ${styles.cancelButton}`} onClick={triggerClose}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateClassroomModal;